import React, { useEffect } from 'react';
import { useAppDispatch } from 'hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { Select } from '.';
import { InputProps } from '../props';

const CascadingSelectors: React.FC<InputProps> = ({ value = {}, onChange, cascadingSelectors = [] }) => {
  const dispatch = useAppDispatch();

  // Sort cascadingSelectors by position to ensure proper order
  const sortedSelectors = [...cascadingSelectors].sort((a, b) => a.position - b.position);

  // Calculate dynamic CSSProperties for visible selectors
  const style = {
    '--visible-selectors': cascadingSelectors.filter(
      (_, index) => index === 0 || value[cascadingSelectors[index - 1].id],
    ).length,
  } as React.CSSProperties;

  const selectorOptions = sortedSelectors.reduce(
    (acc, selector) => {
      acc[selector.id] = useSelector((state: RootState) => selector.selectorOptions(state));
      return acc;
    },
    {} as Record<string, any[]>,
  );

  // Handle changes for any select in the hierarchy
  const handleChange = (name: string, val: any) => {
    const selector = sortedSelectors.find((sel) => sel.name === name);
    if (!selector) return;

    const { id, fKey = 'id' } = selector;

    // Update the current value
    onChange && onChange(id, val);

    // Clear options for all lower selectors
    const lowerSelectors = sortedSelectors.filter((s) => s.position > selector.position);
    lowerSelectors.forEach(({ id, reducerFunctions }) => {
      if (reducerFunctions?.clear) {
        dispatch(reducerFunctions.clear([]));
      }

      // update lower selector values
      onChange && onChange(id, undefined);
    });

    // Fetch new options for the next selector in the chain
    const nextSelector = sortedSelectors.find((s) => s.position === selector.position + 1);
    if (nextSelector && val && nextSelector.selectorActions) {
      const params = { [fKey]: val }; // Use dynamic key for link
      dispatch(nextSelector.selectorActions(params));
    }
  };

  const firstSelector = sortedSelectors.find((s) => s.position === 1);
  const paramKeys =
    typeof firstSelector?.selectorParams === 'function' ? Object.keys(firstSelector.selectorParams(value) || {}) : [];
  const paramValues = paramKeys.map((key) => value[key]);

  useEffect(() => {
    if (!firstSelector) return;
    const { selectorActions, selectorParams } = firstSelector;
    const params = typeof selectorParams === 'function' ? selectorParams(value) : (selectorParams ?? {});

    const hasAllParams = Object.values(params).every((v) => v !== undefined && v !== null);
    if (hasAllParams && selectorActions) {
      dispatch(selectorActions(params));
    }
  }, [firstSelector, ...paramValues]);

  useEffect(() => {
    sortedSelectors.forEach(({ id, selectTop }) => {
      if (selectTop && value[id] === undefined) {
        const options = selectorOptions[id];
        if (options && options.length > 0) {
          handleChange(id, options[0].id);
        }
      }
    });
  }, [selectorOptions]);

  return (
    <div className="cascading-selector" style={style}>
      {cascadingSelectors.map((selector, index) => {
        const { id, label, selectorOptions } = selector;

        const isVisible = index === 0 || value[cascadingSelectors[index - 1].id];
        return (
          isVisible && (
            <div key={id} className={`cascading-selector-container`}>
              {label && (
                <label htmlFor={id} className="cascading-label">
                  {label}:
                </label>
              )}
              <div className="cascading-select">
                <Select
                  type="select"
                  id={id}
                  name={id}
                  value={value[id] || undefined}
                  onChange={(name, val) => handleChange(name, val)}
                  selectorOptions={selectorOptions}
                  className="w-100"
                />
              </div>
            </div>
          )
        );
      })}
    </div>
  );
};

export default CascadingSelectors;
